/**
 * 🧮 Página de Gestión de CMO (Costo de Mano de Obra)
 *
 * Permite:
 * - Importar archivos Excel con novedades de empleados
 * - Ver historial de importaciones
 * - Visualizar reportes detallados por importación
 * - Ver resumen de costos por centro de costo
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  IconButton,
  Chip,
  Grid
} from '@mui/material';
import {
  CloudUpload,
  Visibility,
  Delete,
  Assessment,
  Business,
  AccountBalance,
  AttachMoney,
  Download,
  PostAdd
} from '@mui/icons-material';
import { API_BASE_URL } from '../../config';
import GestionCentrosCosto from './GestionCentrosCosto';
import GestionAdicionalesReferentes from './GestionAdicionalesReferentes';
import CargaNovedades from './CargaNovedades';

const CMO = () => {
  const [tabValue, setTabValue] = useState(0);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Estado para importaciones
  const [importaciones, setImportaciones] = useState([]);
  const [selectedImportacion, setSelectedImportacion] = useState(null);
  const [reporteDetalle, setReporteDetalle] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Estado para resumen por centros
  const [resumenCentros, setResumenCentros] = useState([]);

  useEffect(() => {
    cargarImportaciones();
    cargarResumenCentros();
  }, []);

  const cargarImportaciones = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/cmo/importaciones`, {
        withCredentials: true
      });
      setImportaciones(response.data);
    } catch (error) {
      console.error('Error al cargar importaciones:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Error al cargar importaciones'
      });
    } finally {
      setLoading(false);
    }
  };

  const cargarResumenCentros = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/cmo/resumen-centros`, {
        withCredentials: true
      });
      setResumenCentros(response.data);
    } catch (error) {
      console.error('Error al cargar resumen de centros:', error);
    }
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];

    if (selectedFile) {
      // Validar que sea Excel
      const validExtensions = ['.xlsx', '.xls'];
      const fileExtension = selectedFile.name.substring(selectedFile.name.lastIndexOf('.')).toLowerCase();

      if (!validExtensions.includes(fileExtension)) {
        setMessage({
          type: 'error',
          text: 'Por favor seleccione un archivo Excel (.xlsx o .xls)'
        });
        return;
      }

      setFile(selectedFile);
      setMessage({ type: '', text: '' });
    }
  };

  const handleImport = async () => {
    if (!file) {
      setMessage({ type: 'error', text: 'Por favor seleccione un archivo' });
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      setImporting(true);
      setMessage({ type: '', text: '' });

      const response = await axios.post(`${API_BASE_URL}/api/cmo/importar`, formData, {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setMessage({
        type: 'success',
        text: `Importación completada. ${response.data.estadisticas.exitosos} registros procesados correctamente${response.data.estadisticas.errores > 0 ? `, ${response.data.estadisticas.errores} errores` : ''}.`
      });

      setFile(null);
      document.getElementById('file-input').value = '';

      // Recargar lista de importaciones
      cargarImportaciones();
      cargarResumenCentros();

    } catch (error) {
      console.error('Error al importar:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Error al importar archivo'
      });
    } finally {
      setImporting(false);
    }
  };

  const verReporte = async (importacionId) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/cmo/reporte/${importacionId}`, {
        withCredentials: true
      });

      setReporteDetalle(response.data);
      setDialogOpen(true);
    } catch (error) {
      console.error('Error al cargar reporte:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Error al cargar reporte'
      });
    } finally {
      setLoading(false);
    }
  };

  const eliminarImportacion = async (importacionId) => {
    if (!window.confirm('¿Está seguro de eliminar esta importación? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/api/cmo/importacion/${importacionId}`, {
        withCredentials: true
      });

      setMessage({
        type: 'success',
        text: 'Importación eliminada correctamente'
      });

      cargarImportaciones();
      cargarResumenCentros();
    } catch (error) {
      console.error('Error al eliminar:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Error al eliminar importación'
      });
    }
  };

  const formatearFecha = (fecha) => {
    return new Date(fecha).toLocaleString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatearMoneda = (valor) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(valor);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        🧮 Costo de Mano de Obra (CMO)
      </Typography>

      {message.text && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage({ type: '', text: '' })}>
          {message.text}
        </Alert>
      )}

      <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
        <Tab label="Importar" icon={<CloudUpload />} iconPosition="start" />
        <Tab label="Importaciones" icon={<Assessment />} iconPosition="start" />
        <Tab label="Resumen por Centro" icon={<Business />} iconPosition="start" />
        <Tab label="Centros de Costo" icon={<AccountBalance />} iconPosition="start" />
        <Tab label="Adicionales" icon={<AttachMoney />} iconPosition="start" />
        <Tab label="Carga de Novedades" icon={<PostAdd />} iconPosition="start" />
      </Tabs>

      {/* Tab 1: Importar */}
      {tabValue === 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Importar archivo de novedades
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Seleccione un archivo Excel (.xlsx o .xls) con las novedades de los empleados.
              El archivo debe contener las columnas: Legajo, Nombre, CentroA, CentroB (opcional),
              y las columnas de conceptos (Ha, Re, Co).
            </Typography>

            <Box sx={{ mb: 2 }}>
              <input
                accept=".xlsx, .xls"
                style={{ display: 'none' }}
                id="file-input"
                type="file"
                onChange={handleFileChange}
              />
              <label htmlFor="file-input">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUpload />}
                >
                  Seleccionar archivo
                </Button>
              </label>

              {file && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Archivo seleccionado: <strong>{file.name}</strong> ({(file.size / 1024).toFixed(2)} KB)
                </Typography>
              )}
            </Box>

            <Button
              variant="contained"
              color="primary"
              onClick={handleImport}
              disabled={!file || importing}
              startIcon={importing ? <CircularProgress size={20} /> : <CloudUpload />}
            >
              {importing ? 'Importando...' : 'Importar'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Tab 2: Lista de Importaciones */}
      {tabValue === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Historial de importaciones
            </Typography>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : importaciones.length === 0 ? (
              <Alert severity="info">No hay importaciones registradas</Alert>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Fecha</TableCell>
                      <TableCell>Archivo</TableCell>
                      <TableCell>Usuario</TableCell>
                      <TableCell align="center">Total</TableCell>
                      <TableCell align="center">Exitosos</TableCell>
                      <TableCell align="center">Errores</TableCell>
                      <TableCell align="center">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {importaciones.map((imp) => (
                      <TableRow key={imp.id}>
                        <TableCell>{formatearFecha(imp.fecha_importacion)}</TableCell>
                        <TableCell>{imp.nombre_archivo}</TableCell>
                        <TableCell>{imp.usuario_importacion}</TableCell>
                        <TableCell align="center">{imp.total_registros}</TableCell>
                        <TableCell align="center">
                          <Chip label={imp.registros_exitosos} color="success" size="small" />
                        </TableCell>
                        <TableCell align="center">
                          {imp.registros_error > 0 && (
                            <Chip label={imp.registros_error} color="error" size="small" />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => verReporte(imp.id)}
                            title="Ver reporte"
                          >
                            <Visibility />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => eliminarImportacion(imp.id)}
                            title="Eliminar"
                          >
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tab 3: Resumen por Centro de Costo */}
      {tabValue === 2 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Resumen de costos por centro de costo
            </Typography>

            {resumenCentros.length === 0 ? (
              <Alert severity="info">No hay datos para mostrar</Alert>
            ) : (
              <Grid container spacing={2}>
                {resumenCentros.map((centro, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" color="primary">
                          {centro.centro || 'Sin centro'}
                        </Typography>
                        <Typography variant="h5" sx={{ my: 1 }}>
                          {formatearMoneda(centro.costoTotal)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {centro.importaciones} importación{centro.importaciones !== 1 ? 'es' : ''}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tab 4: Centros de Costo */}
      {tabValue === 3 && <GestionCentrosCosto />}

      {/* Tab 5: Adicionales y Referentes */}
      {tabValue === 4 && <GestionAdicionalesReferentes />}

      {/* Tab 6: Carga de Novedades */}
      {tabValue === 5 && <CargaNovedades />}

      {/* Dialog de reporte detallado */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Detalle de importación
        </DialogTitle>
        <DialogContent>
          {reporteDetalle && (
            <>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Archivo: {reporteDetalle.importacion.nombre_archivo}
                </Typography>
                <Typography variant="subtitle2" color="text.secondary">
                  Fecha: {formatearFecha(reporteDetalle.importacion.fecha_importacion)}
                </Typography>
              </Box>

              <Typography variant="h6" gutterBottom>
                Totales generales
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={4} md={2} lg={1.2}>
                  <Card variant="outlined">
                    <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                      <Typography color="text.secondary" variant="caption" sx={{ fontSize: '0.7rem' }}>Empleados</Typography>
                      <Typography variant="h6" sx={{ fontSize: '1rem' }}>{reporteDetalle.totales.empleados}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={4} md={2} lg={1.2}>
                  <Card variant="outlined">
                    <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                      <Typography color="text.secondary" variant="caption" sx={{ fontSize: '0.7rem' }}>Haberes</Typography>
                      <Typography variant="h6" sx={{ fontSize: '1rem' }}>{formatearMoneda(reporteDetalle.totales.totalHaberes)}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={4} md={2} lg={1.2}>
                  <Card variant="outlined">
                    <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                      <Typography color="text.secondary" variant="caption" sx={{ fontSize: '0.7rem' }}>Contribuciones</Typography>
                      <Typography variant="h6" sx={{ fontSize: '1rem' }}>{formatearMoneda(reporteDetalle.totales.totalContribuciones)}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={4} md={2} lg={1.2}>
                  <Card variant="outlined" sx={{ borderColor: 'warning.light' }}>
                    <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                      <Typography color="text.secondary" variant="caption" sx={{ fontSize: '0.7rem' }}>Provisión</Typography>
                      <Typography variant="h6" sx={{ fontSize: '1rem' }} color="warning.main">{formatearMoneda(reporteDetalle.totales.totalProvision)}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={4} md={2} lg={1.2}>
                  <Card variant="outlined">
                    <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                      <Typography color="text.secondary" variant="caption" sx={{ fontSize: '0.7rem' }}>Adicional</Typography>
                      <Typography variant="h6" sx={{ fontSize: '1rem' }}>{formatearMoneda(reporteDetalle.totales.totalAdicional)}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={4} md={2} lg={1.2}>
                  <Card variant="outlined">
                    <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                      <Typography color="text.secondary" variant="caption" sx={{ fontSize: '0.7rem' }}>Complemento</Typography>
                      <Typography variant="h6" sx={{ fontSize: '1rem' }}>{formatearMoneda(reporteDetalle.totales.totalComplemento)}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={4} md={2} lg={1.2}>
                  <Card variant="outlined" sx={{ borderColor: 'warning.light' }}>
                    <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                      <Typography color="text.secondary" variant="caption" sx={{ fontSize: '0.7rem' }}>Prov. Compl.</Typography>
                      <Typography variant="h6" sx={{ fontSize: '1rem' }} color="warning.main">{formatearMoneda(reporteDetalle.totales.totalProvisionComplemento)}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={4} md={2} lg={1.2}>
                  <Card variant="outlined">
                    <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                      <Typography color="text.secondary" variant="caption" sx={{ fontSize: '0.7rem' }}>Obra Social</Typography>
                      <Typography variant="h6" sx={{ fontSize: '1rem' }}>{formatearMoneda(reporteDetalle.totales.totalObraSocial)}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={4} md={2} lg={1.2}>
                  <Card variant="outlined" sx={{ borderColor: 'info.main' }}>
                    <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                      <Typography color="text.secondary" variant="caption" sx={{ fontSize: '0.7rem' }}>Subtotal</Typography>
                      <Typography variant="h6" sx={{ fontSize: '1rem' }} color="info.main">{formatearMoneda(reporteDetalle.totales.subtotal)}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={4} md={2} lg={1.2}>
                  <Card variant="outlined" sx={{ borderColor: 'primary.main', bgcolor: 'primary.50' }}>
                    <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                      <Typography color="text.secondary" variant="caption" sx={{ fontSize: '0.7rem' }}>TOTAL</Typography>
                      <Typography variant="h6" sx={{ fontSize: '1rem' }} color="primary.main">
                        {formatearMoneda(reporteDetalle.totales.total)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Resumen por centro de costo
              </Typography>
              <TableContainer component={Paper} sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Centro</TableCell>
                      <TableCell align="right">Empleados</TableCell>
                      <TableCell align="right">Subtotal</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reporteDetalle.resumenPorCentro.map((centro, index) => (
                      <TableRow key={index}>
                        <TableCell>{centro.centro || 'Sin centro'}</TableCell>
                        <TableCell align="right">{centro.empleados}</TableCell>
                        <TableCell align="right">{formatearMoneda(centro.costoTotal)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography variant="h6" gutterBottom>
                Detalle por empleado
              </Typography>
              <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: '0.75rem', p: 1 }}>Legajo</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', p: 1 }}>Nombre</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', p: 1 }}>Centro de Costo</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem', p: 1 }}>Haberes</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem', p: 1 }}>Contribuc.</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem', p: 1, color: 'warning.main' }}>Provisión</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem', p: 1, color: 'info.main' }}>Subtotal</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem', p: 1 }}>Adicional</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem', p: 1 }}>Compl.</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem', p: 1, color: 'warning.main' }}>Prov.C.</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem', p: 1 }}>O.Social</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem', p: 1, color: 'primary.main', fontWeight: 'bold' }}>TOTAL</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', p: 1 }}>Referente</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem', p: 1 }}>Neto</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reporteDetalle.detalles.flatMap((detalle, index) => {
                      const dists = detalle.distribuciones && detalle.distribuciones.length > 0
                        ? detalle.distribuciones
                        : [{ centro: 'Sin centro', porcentaje: 100 }];
                      return dists.map((dist, i) => {
                        const pct = (dist.porcentaje || 100) / 100;
                        const esPrimera = i === 0;
                        const esPartido = dists.length > 1;
                        return (
                          <TableRow
                            key={`${index}-${i}`}
                            sx={esPartido && !esPrimera ? { backgroundColor: 'action.hover' } : {}}
                          >
                            <TableCell sx={{ fontSize: '0.75rem', p: 1 }}>{detalle.legajo}</TableCell>
                            <TableCell sx={{ fontSize: '0.75rem', p: 1 }}>{detalle.nombre}</TableCell>
                            <TableCell sx={{ fontSize: '0.75rem', p: 1 }}>
                              {esPartido ? `${dist.centro} (${dist.porcentaje}%)` : dist.centro}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.75rem', p: 1 }}>{formatearMoneda(detalle.total_haberes * pct)}</TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.75rem', p: 1 }}>{formatearMoneda(detalle.total_contribuciones * pct)}</TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.75rem', p: 1, color: 'warning.main' }}>{formatearMoneda(detalle.provision * pct)}</TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.75rem', p: 1, color: 'info.main' }}>{formatearMoneda(detalle.subtotal * pct)}</TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.75rem', p: 1 }}>{formatearMoneda(detalle.adicional * pct)}</TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.75rem', p: 1 }}>{formatearMoneda(detalle.complemento * pct)}</TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.75rem', p: 1, color: 'warning.main' }}>{formatearMoneda(detalle.provision_complemento * pct)}</TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.75rem', p: 1 }}>{formatearMoneda(detalle.obra_social * pct)}</TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.75rem', p: 1 }}><strong>{formatearMoneda(detalle.total * pct)}</strong></TableCell>
                            <TableCell sx={{ fontSize: '0.75rem', p: 1 }}>{esPrimera ? (detalle.referente_nombre || '-') : ''}</TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.75rem', p: 1 }}>{formatearMoneda(detalle.neto_a_pagar * pct)}</TableCell>
                          </TableRow>
                        );
                      });
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (!reporteDetalle) return;
              const rows = reporteDetalle.detalles.flatMap(d => {
                const dists = d.distribuciones && d.distribuciones.length > 0
                  ? d.distribuciones
                  : [{ centro: 'Sin centro', porcentaje: 100 }];
                return dists.map((dist, i) => {
                  const pct = (dist.porcentaje || 100) / 100;
                  return {
                    Legajo: d.legajo,
                    Nombre: d.nombre,
                    'Centro de Costo': dists.length > 1 ? `${dist.centro} (${dist.porcentaje}%)` : dist.centro,
                    Haberes: parseFloat((d.total_haberes * pct).toFixed(2)),
                    Contribuciones: parseFloat((d.total_contribuciones * pct).toFixed(2)),
                    Provision: parseFloat((d.provision * pct).toFixed(2)),
                    Subtotal: parseFloat((d.subtotal * pct).toFixed(2)),
                    Adicional: parseFloat((d.adicional * pct).toFixed(2)),
                    Complemento: parseFloat((d.complemento * pct).toFixed(2)),
                    'Prov. Complemento': parseFloat((d.provision_complemento * pct).toFixed(2)),
                    'Obra Social': parseFloat((d.obra_social * pct).toFixed(2)),
                    Total: parseFloat((d.total * pct).toFixed(2)),
                    Referente: i === 0 ? (d.referente_nombre || '') : '',
                    'Neto a Pagar': parseFloat((d.neto_a_pagar * pct).toFixed(2))
                  };
                });
              });
              const archivo = reporteDetalle.importacion.nombre_archivo.replace(/\.[^.]+$/, '');
              const colNames = Object.keys(rows[0] || {});
              const blueColIndexes = [colNames.indexOf('Subtotal'), colNames.indexOf('Total')];

              const wb2 = new ExcelJS.Workbook();
              const ws2 = wb2.addWorksheet('Detalle');

              // Encabezados en negrita
              ws2.addRow(colNames);
              ws2.getRow(1).eachCell((cell, colIdx) => {
                cell.font = { bold: true };
                if (blueColIndexes.includes(colIdx - 1)) {
                  cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1565C0' } };
                  cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
                } else {
                  cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0E0E0' } };
                }
                cell.border = { bottom: { style: 'medium' } };
              });

              // Filas de datos
              rows.forEach(row => {
                const dataRow = ws2.addRow(colNames.map(k => row[k]));
                dataRow.eachCell((cell, colIdx) => {
                  const isMoneda = typeof cell.value === 'number';
                  if (isMoneda) cell.numFmt = '"$"#,##0.00';
                  if (blueColIndexes.includes(colIdx - 1)) {
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE3F2FD' } };
                    if (isMoneda) cell.font = { bold: true };
                  }
                });
              });

              // Ancho automático de columnas
              ws2.columns.forEach((col, i) => {
                col.width = Math.max((colNames[i] || '').length + 2, 14);
              });

              wb2.xlsx.writeBuffer().then(buffer => {
                const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `CMO_${archivo}.xlsx`;
                a.click();
                URL.revokeObjectURL(url);
              });
            }}
            variant="contained"
            startIcon={<Download />}
            color="success"
          >
            Exportar Excel
          </Button>
          <Button onClick={() => setDialogOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CMO;
